import { Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { SyncTooltipContainer } from '@/console/features/groups/components/group-sync-status/group-sync-status-tooltip/group-sync-status-tooltip.styled';
import { TSyncStatusTooltipProps } from '@/console/features/groups/components/group-sync-status/group-sync-status-tooltip/group-sync-status-tooltip.types';
import { SyncStatusIcon } from '@/console/features/groups/components/group-sync-status/group-sync-status.styled';
import { Tooltip } from '@/shared/components/ui/tooltip/tooltip';
import { TruncatedText } from '@/shared/components/ui/truncated-text/truncated-text';
import { useDateUtils } from '@/shared/hooks/use-date-utils/use-date-utils';
import { DeviceSyncStatusEnum } from '@/shared/types/types.generated';

export const GroupSyncStatusTooltip: React.FC<TSyncStatusTooltipProps> = ({
  navigateToAssetPath,
  assetName,
  syncStatusTimestamp,
  assetId,
  status,
}) => {
  const { t } = useTranslation();
  const { formatUpdatedTimestamp } = useDateUtils();
  const timestamp = formatUpdatedTimestamp(syncStatusTimestamp);
  const navigate = useNavigate();

  const navigateToAsset = (event: React.BaseSyntheticEvent) => {
    event.stopPropagation();
    navigate(navigateToAssetPath(assetId));
  };

  const getSyncStatusIconVariant = () => {
    switch (status) {
      case DeviceSyncStatusEnum.Error:
        return 'error';
      case DeviceSyncStatusEnum.Synced:
        return 'success';
      case DeviceSyncStatusEnum.NotSynced:
        return 'warning';
      default:
        return 'light';
    }
  };

  const getSyncStatusTooltipTitle = () => {
    if (status) {
      return t(`projects:sync_status_enum.${status.toLowerCase()}`);
    }

    return t('projects:sync_status_enum.unknown');
  };

  return (
    <SyncTooltipContainer
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      gap="0.5rem"
      onClick={navigateToAsset}
    >
      <TruncatedText variant="p7" text={assetName} maxLength={12} />
      <Stack
        direction="row"
        gap="0.375rem"
        alignItems="center"
        justifyContent="flex-end"
      >
        <Typography variant="p7">{timestamp}</Typography>
        {status && (
          <Tooltip title={getSyncStatusTooltipTitle()} arrow>
            <SyncStatusIcon
              variant={getSyncStatusIconVariant()}
              size="0.875rem"
            />
          </Tooltip>
        )}
      </Stack>
    </SyncTooltipContainer>
  );
};
